"use client";

import type { ComponentInfo } from "@builder.io/sdk-react";

import {
  getLoyaltyDiscountBadgeCopyBuilderInput,
  getLoyaltyRewardCopyAuthoringToggleBuilderInput,
  getLoyaltyRewardCopyBuilderInput,
  type LoyaltyDiscountBadgeCopyInput,
  type LoyaltyRewardCopyInput,
} from "@/libs/builder/ProductDetailsLoyaltyBuilderInputs";

export const CONSUMABLES_PRODUCT_DETAILS_CONTAINER_INPUTS = [
  {
    name: "hasBadge",
    type: "boolean",
  },
  {
    name: "overwriteBadgeText",
    type: "string",
  },
  {
    name: "hasDiscountBadge",
    type: "boolean",
  },
  {
    name: "hasLoyaltyDiscountBadge",
    type: "boolean",
    defaultValue: false,
    helperText: "Controls the loyalty price tag visibility independently from the -X% discount badge.",
  },
  getLoyaltyDiscountBadgeCopyBuilderInput(),
  {
    name: "hasStrikethroughPrice",
    type: "boolean",
  },
  {
    name: "hideCta",
    type: "boolean",
  },
  {
    name: "hidePrice",
    type: "boolean",
  },
  {
    name: "isPriceDropdownEnabled",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "priceDropdownPromotionalMessage",
    showIf: `options.get("isPriceDropdownEnabled") === true`,
    type: "string",
  },
  {
    name: "priceDropdownPromotionalTooltip",
    showIf: `options.get("isPriceDropdownEnabled") === true`,
    type: "longText",
  },
  getLoyaltyRewardCopyAuthoringToggleBuilderInput(),
  getLoyaltyRewardCopyBuilderInput(),
  {
    name: "productCta",
    type: "object",
    helperText:
      "Product CTA for not in stock products and commerce disabled tenants. Overwrites default configuration from Commerce Global Settings.",
    subFields: [
      {
        name: "label",
        type: "string",
      },
      {
        name: "url",
        type: "url",
      },
    ],
    defaultValue: {
      label: "",
      url: "",
    },
  },
] as const satisfies ComponentInfo["inputs"];

export const CONSUMABLES_PRODUCT_DETAILS_CONTAINER_BINDINGS_INPUTS = [
  {
    name: "sku",
    type: "string",
  },
  {
    name: "badgeText",
    type: "string",
  },
  {
    name: "description",
    type: "string",
  },
  {
    name: "shortDescription",
    type: "string",
  },
  {
    name: "knowMoreUrl",
    type: "string",
  },
  {
    name: "multiPackDescription",
    type: "string",
  },
  {
    name: "productName",
    type: "string",
  },
  {
    name: "isCommerceDisabled",
    type: "boolean",
  },
  {
    name: "isMultiPackEnabled",
    type: "boolean",
  },
  {
    name: "multiPackName",
    type: "string",
  },
  {
    name: "multiPackProducts",
    type: "list",
  },
  {
    name: "customBuyCtaLabel",
    type: "string",
  },
  {
    name: "consumablesMatrixData",
    type: "object",
    subFields: [
      {
        name: "packagingFormats",
        type: "list",
      },
      {
        name: "items",
        type: "list",
      },
    ],
  },
  {
    name: "isSubscriptionsEnabled",
    type: "boolean",
  },
] as const satisfies ComponentInfo["inputs"];

export const ALL_CONSUMABLES_PRODUCT_DETAILS_CONTAINER_INPUTS = [
  ...CONSUMABLES_PRODUCT_DETAILS_CONTAINER_INPUTS,
  ...CONSUMABLES_PRODUCT_DETAILS_CONTAINER_BINDINGS_INPUTS,
] as const satisfies ComponentInfo["inputs"];

export type ConsumablesProductDetailsContainerProps = BuilderComponentProps<
  typeof ALL_CONSUMABLES_PRODUCT_DETAILS_CONTAINER_INPUTS
> & {
  loyaltyDiscountBadgeCopy?: LoyaltyDiscountBadgeCopyInput;
  loyaltyRewardCopy?: LoyaltyRewardCopyInput;
  isLoyaltyRewardCopyAuthoringEnabled?: boolean;
};
